import React, { forwardRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "goober";
import type { TFunction } from "i18next";

import Container from "@/shared/ContentContainer.jsx";
import {
  ComplexLink,
  dropdownArrow,
  dropdownContainerCss,
  Links,
  LinksLoading,
  linksWithMenus,
} from "@/shared/PageHeader.style.jsx";
import { classNames } from "@/util/class-names.mjs";
import { useRoute } from "@/util/router-hooks.mjs";
import useDropdownRef from "@/util/use-dropdown-ref.mjs";

const disabledClass = () => css`
  color: var(--shade5);
`;

const classes = {
  active: "type-form--tab current",
  standard: "type-form--tab",
};

const disabledProps = {
  role: "link",
  "aria-disabled": true,
};

export type PageHeaderLink = {
  text?: Translation | string;
  Text?: React.FC | React.JSX.Element;

  onClick?: () => void;
  isNew?: boolean;
  url?: string;
  disabled?: boolean;
  routeMatch?: RegExp | string;
  isDefault?: boolean;
  parent?: string;
  img?: React.FC<{ className: string }>;
  tooltip?: string;
  menu?: PageHeaderLink[];
} & (
  | { text: Translation | string; Text?: never }
  | { text?: never; Text: React.FC | React.JSX.Element }
);

function getTitle(t: TFunction, link: PageHeaderLink): React.ReactNode {
  return link.Text ? (
    link.Text instanceof Function ? (
      <link.Text />
    ) : (
      link.Text
    )
  ) : Array.isArray(link.text) ? (
    t(...link.text)
  ) : (
    link.text
  );
}

export type PageHeaderLinksProps = {
  links?: PageHeaderLink[];
  linksLoading?: boolean;
  counterModeFn?: (
    link: PageHeaderLink,
    currentPath: string,
    searchParams: URLSearchParams,
    classes: {
      active: string;
      standard: string;
    },
  ) => { url: string; class: string };
  navLabel?: string | null;
};

export type SimpleHeaderLinkProps = {
  className?: string;
  Element: "span" | "a";
  link: PageHeaderLink;
};

const SimpleHeaderLink = ({
  children,
  className,
  Element,
  link,
}: React.PropsWithChildren<SimpleHeaderLinkProps>) => {
  const [isDropdownShown, setShowDropdown] = useState(false);
  const hasDropdown = link?.menu?.length > 0;
  const { t } = useTranslation();

  const showDropdown = useCallback(() => {
    if (!hasDropdown) return;
    setShowDropdown(true);
  }, [hasDropdown]);

  const hideDropdown = useCallback(() => {
    if (!hasDropdown) return;
    setShowDropdown(false);
  }, [hasDropdown]);

  const dropdownRef = useDropdownRef({
    onAssignNode: () => {},
    onClickOutside: hideDropdown,
    onWindowResize: hideDropdown,
  });

  const onClick = link.onClick ?? hideDropdown;

  return (
    <li
      ref={dropdownRef}
      onMouseEnter={showDropdown}
      onMouseLeave={hideDropdown}
      {...classNames(hasDropdown && dropdownContainerCss())}
    >
      <Element
        onClick={onClick}
        href={link.disabled ? "#" : link.url}
        className={className}
        data-tooltip={link.tooltip}
        // Some browsers just see the aria-disabled tag and run w/ it no matter what its value is
        {...(link.disabled && disabledProps)}
        aria-current={className === classes.active ? true : null}
        {...classNames(className, hasDropdown && dropdownArrow())}
      >
        {children}
      </Element>
      {hasDropdown && (
        <div {...classNames("dropdown", isDropdownShown && "visible")}>
          {link.menu.map((item, i) => {
            const Component = item.disabled ? "span" : "a";
            const title = getTitle(t, item);

            return (
              <Component
                key={i}
                onClick={item.onClick}
                href={item.disabled ? "#" : item.url}
                data-tooltip={item.tooltip}
                {...(link.disabled && disabledProps)}
              >
                {title}
              </Component>
            );
          })}
        </div>
      )}
    </li>
  );
};

const PageHeaderLinks = (
  {
    links = [],
    linksLoading = false,
    counterModeFn = null,
    navLabel = null,
    ...restProps
  }: PageHeaderLinksProps,
  ref,
) => {
  // e.g. route->currentPath = /xxx/type/tab/
  //      in config            /xxx/type/tab
  //                                        ^ '/' missing
  const { searchParams, currentPath } = useRoute();

  const { t } = useTranslation();

  if (linksLoading) {
    return (
      <Container>
        <Links
          className="links-container"
          ref={ref}
          aria-label={navLabel}
          {...restProps}
        >
          <ul>
            <LinksLoading className="loading-row" />
          </ul>
        </Links>
      </Container>
    );
  }

  if (!Array.isArray(links) || !links.length) return null;

  const hasAnyMenus = links.some((link) => link?.menu?.length > 0);

  return (
    <Container {...classNames(hasAnyMenus && linksWithMenus())}>
      <Links
        className="links-container"
        ref={ref}
        aria-label={navLabel}
        {...restProps}
      >
        <ul>
          {links.map((link, i) => {
            // Determine active vs standard navigation state
            let className = classes.standard;

            if (link.routeMatch instanceof RegExp) {
              // RegExp detection
              const entries = searchParams.toString();
              let fullPath = currentPath;
              if (entries.length) fullPath += `?${entries}`;
              if (link.routeMatch.test(fullPath)) className = classes.active;
            } else {
              // Standard detection
              const exactMatch =
                currentPath === link.url || currentPath === `${link.url}/`;
              const noMatch = links.every((i) => i.url !== currentPath);
              if (
                exactMatch ||
                (noMatch && link.isDefault) ||
                (link.parent && currentPath.startsWith(link.parent))
              )
                className = classes.active;
            }

            let link_url = link.url;

            if (counterModeFn) {
              // e.g. /game/types/X/o
              // e.g. /game/types/X/o/Y
              const counter = counterModeFn(
                link,
                currentPath,
                searchParams,
                classes,
              );

              if (counter) {
                link_url = counter.url;
                className = counter.class;
              }
            }

            if (link.disabled) className += ` ${disabledClass()}`;

            const title = getTitle(t, link);

            const Element = link.disabled ? "span" : "a";

            if (link.img) {
              const Img = link.img;

              return (
                <li key={i}>
                  <ComplexLink data-tooltip={link.tooltip}>
                    <Img className={className} />
                    <Element
                      onClick={link.onClick}
                      href={link.disabled ? "#" : link_url}
                      className={className}
                      {...(link.disabled && disabledProps)}
                      aria-current={className === classes.active ? true : null}
                    >
                      {title}
                      {link.isNew && (
                        <span className="new-badge type-overline">
                          {t("common:new", "New")}
                        </span>
                      )}
                    </Element>
                  </ComplexLink>
                </li>
              );
            }

            // Render
            return (
              <SimpleHeaderLink
                key={i}
                Element={Element}
                link={link}
                className={className}
              >
                {title}
                {link.isNew && (
                  <span className="new-badge type-overline">
                    {t("common:new", "New")}
                  </span>
                )}
              </SimpleHeaderLink>
            );
          })}
        </ul>
      </Links>
    </Container>
  );
};

export default forwardRef(PageHeaderLinks);
